// C:\Helpdesk\frontend\src\App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
// Remove 'notification' from this import, as it's now handled by NotificationContext
import { ConfigProvider, App as AntdApp, theme } from 'antd'; 
import { StyleProvider } from '@ant-design/cssinjs';

// CRITICAL: Import Ant Design's global reset CSS
import 'antd/dist/reset.css'; 

// Import the new NotificationProvider
import { NotificationProvider } from './context/NotificationContext'; // <--- NEW IMPORT

// Auth Pages
import Login from './pages/auth/login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Main Pages
import ContactAdministrator from './pages/contact/ContactAdministrator';
import PrivateRoute from './pages/components/PrivateRoute';
import AdminRoute from './pages/components/AdminRoute';
import Dashboard from './pages/dashboard/dashboard';
import PartnerDashboard from './pages/dashboard/PartnerDashboard';
import Reports from './pages/Reports/Reports';
import ReportsHub from './pages/Reports/ReportsHub';
import MainLayout from './layout/MainLayout/MainLayout';

// Analytics/Report Pages
import ContractTicketVolume from './pages/Analytics/ContractTicketVolume';
import SummaryBorangAduan from './pages/Analytics/SummaryBorangAduan';

//Reports/Summary Service Report Pages
import SummaryServiceReport from './pages/Reports/SummaryServiceReport';
import SummaryServiceReportEdit from './pages/Reports/SummaryServiceReportEdit';

// CR/CM Report Pages
import ReportGeneratorSingle from './pages/TimeTracking/ReportGeneratorSingle';
import ReportGeneratorMonthly from './pages/TimeTracking/ReportGeneratorMonthly';
import ManDayReport from './pages/TimeTracking/ManDayReport';
import ReportGeneratorCR from './pages/Reports/ReportGeneratorCR';

// NEW: Import ProfilePage
import ProfilePage from './pages/ProfilePage'; // Ensure this path is correct

// Admin Pages
import UserAccessPage from './pages/admin/UserAccessPage';
import ServiceCatalogPage from './pages/admin/ServiceCatalogPage';
import TicketSettingsPage from './pages/admin/TicketSettingsPage';
import SystemAutomationPage from './pages/admin/SystemAutomationPage';

// 🆕 Client Computing Portal
import ClientAssets from './pages/ClientComputing/ClientAssets';

// 🆕 CR Management
import CRListPage from './pages/CRManagement/CRListPage';
import CreateCRPage from './pages/CRManagement/CreateCRPage';
import CRDetailPage from './pages/CRManagement/CRDetailPage';

// Ticket Pages
import CreateTicketForm from './pages/Tickets/CreateTicketForm';
import TicketsList from './pages/Tickets/TicketsList';
import EditTicketForm from './pages/Tickets/EditTicketForm';
import MyTickets from './pages/Tickets/MyTickets';
import PartnerTicketsList from './pages/Tickets/PartnerTickets/PartnerTicketsList';
import PartnerMyTickets from './pages/Tickets/PartnerTickets/PartnerMyTickets';

import CRManagement from './pages/TimeTracking/CRManagement';
import ActivityManagement from './pages/TimeTracking/ActivityManagement';
import TimeEntryForm from './pages/TimeTracking/TimeEntryForm';

import KnowledgeBaseHome from './pages/KnowledgeBase/KnowledgeBaseHome';
import CreateArticlePage from './pages/KnowledgeBase/CreateArticlePage';
import ArticleViewPage from './pages/KnowledgeBase/ArticleViewPage';
import ArticleEditPage from './pages/KnowledgeBase/ArticleEditPage';

// Footer Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ContactUs from './pages/contact/ContactUs';

// CSS
import './App.css';
import './layout/MainLayout/layout.css';
import OrganizationTickets from './pages/Tickets/OrganizationTickets';

function App() {
  // REMOVED: const [notificationApi, contextHolder] = notification.useNotification();
  // REMOVED: notification.config useEffect is now handled by NotificationProvider
  
  return (
    <StyleProvider hashPriority="high">
      <ConfigProvider
        theme={{
          algorithm: theme.defaultAlgorithm,
          token: {
            fontFamily: `'Inter', sans-serif`,
            fontSize: 15,
            colorPrimary: '#1890ff',
            colorLink: '#1890ff',
            borderRadius: 4,
            zIndexPopupBase: 2000,
            sizeStep: 4,
            sizeUnit: 4,
          },
          components: {
            Layout: {
              headerBg: '#001529',
              headerPadding: '0 24px',
              headerHeight: 64,
              siderBg: '#001529',
            },
            Menu: {
              itemHeight: 48,
              itemFontSize: 15,
              itemColor: 'rgba(255, 255, 255, 0.65)',
              itemHoverColor: '#fff',
              itemSelectedColor: '#fff',
              itemSelectedBg: '#1890ff',
              itemMarginInline: 8,
              itemPaddingInline: 16,
            },
            Tabs: {
              itemFontSize: 15,
              itemSelectedColor: '#1890ff',
              itemHoverColor: '#40a9ff',
              inkBarColor: '#1890ff',
              horizontalItemPadding: '16px 12px',
              horizontalMargin: '0 0 16px 0',
            },
            Table: {
              headerBg: '#fafafa',
              headerColor: 'rgba(0, 0, 0, 0.88)',
              headerBorderRadius: 4,
              fontSize: 15,
            },
            Form: {
              labelFontSize: 15,
            },
            Notification: {
              zIndexPopup: 9999, 
              top: 24,
              bottom: 24,
            },
            Modal: {
              zIndexPopup: 3000,
            },
            Message: { 
              zIndexPopup: 9998,
            },
            Popconfirm: {
              zIndexPopup: 2001,
            },
          },
        }}
        getPopupContainer={() => document.body}
      >
        <AntdApp>
          {/* REMOVED: {contextHolder} - now handled by NotificationProvider */}
          {/* Wrap AuthProvider and AppProvider with NotificationProvider */}
          <NotificationProvider> {/* <--- NEW WRAPPER */}
            <Router>
              <AuthProvider>
                <AppProvider>
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                    <Route path="/contact-administrator" element={<ContactAdministrator />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/contact-us" element={<ContactUs />} />

                    {/* Protected Routes */}
                    <Route element={<PrivateRoute />}>
                      <Route element={<MainLayout />}>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/dashboard/partner" element={<PartnerDashboard />} />
                        <Route path="/reports" element={<ReportsHub />} />
                        <Route path="/reports-legacy" element={<Reports />} />
                        <Route path="/reports/contract-ticket-volume" element={<ContractTicketVolume />} />
                        <Route path="/reports/summary-borang-aduan" element={<SummaryBorangAduan />} />
                        <Route path="/reports/summary-service-report" element={<SummaryServiceReport />} />
                        <Route path="/reports/manday" element={<ManDayReport />} />
                        <Route path="/reports/cr" element={<ReportGeneratorCR />} />
                        <Route path="/time-tracking/reports/single" element={<ReportGeneratorSingle />} />
                        <Route path="/time-tracking/reports/monthly" element={<ReportGeneratorMonthly />} />
                        <Route path="/reports/summary-service-report/:id" element={<SummaryServiceReportEdit />} />
                        <Route path="/tickets" element={<TicketsList />} />
                        {/* Partner Routes for Main Contractors */}
                        <Route path="/partner/tickets" element={<PartnerTicketsList />} />
                        <Route path="/partner/my-tickets" element={<PartnerMyTickets />} />
                        <Route path="/tickets/new" element={<CreateTicketForm />} />
                        <Route path="/tickets/:ticketId" element={<EditTicketForm />} />
                        <Route path="/my-tickets" element={<MyTickets />} />
                        <Route path="/time-tracking/cr" element={<CRManagement />} />
                        <Route path="/time-tracking/activities" element={<ActivityManagement />} />
                        <Route path="/time-tracking/new" element={<TimeEntryForm />} />
                        
                        {/* NEW: Route for ProfilePage */}
                        <Route path="/profile" element={<ProfilePage />} />

                        {/* Accessible to all logged-in users */}
                        <Route path="/knowledge-base" element={<KnowledgeBaseHome />} />
                        <Route path="/knowledge-base/new" element={<CreateArticlePage />} />
                        <Route path="/knowledge-base/:id" element={<ArticleViewPage />} />
                        <Route path="/knowledge-base/edit/:id" element={<ArticleEditPage />} />
                        {/* ======================================== */}

                        {/* Admin Routes */}
                        <Route path="/admin">
                          <Route path="user-access" element={<AdminRoute><UserAccessPage /></AdminRoute>} />
                          <Route path="service-catalog" element={<AdminRoute><ServiceCatalogPage /></AdminRoute>} />
                          <Route path="ticket-settings" element={<AdminRoute><TicketSettingsPage /></AdminRoute>} />
                          <Route path="system-automation" element={<AdminRoute><SystemAutomationPage /></AdminRoute>} />
                        </Route>
                        {/* 🆕 Client Computing Portal Routes */}
                        <Route path="/client-computing">
                          {/* Admin pages — restricted */}
                          <Route path="assets" element={<AdminRoute><ClientAssets /></AdminRoute>} />

                          {/* User pages — coming soon */}
                          {/* <Route path="dashboard" element={<ClientDashboard />} /> */}
                          {/* <Route path="devices" element={<ClientMyDevices />} /> */}
                          {/* <Route path="tickets" element={<ClientMyTickets />} /> */}
                          {/* <Route path="tickets/new" element={<ClientCreateTicketForm />} /> */}
                        </Route>

                        {/* 🆕 CR Management Routes */}
                        <Route path="/cr-management">
                          <Route index element={<CRListPage />} />
                          <Route path="create" element={<CreateCRPage />} />
                          <Route path=":id" element={<CRDetailPage />} />
                        </Route>
                      </Route>
                    </Route>

                    {/* Redirects */}
                    <Route path="/" element={<Navigate to="/login" replace />} />
                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </Routes>
                </AppProvider>
              </AuthProvider>
            </Router>
          </NotificationProvider> {/* <--- END NEW WRAPPER */}
        </AntdApp>
      </ConfigProvider>
    </StyleProvider>
  );
}

export default App;